import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, EcsService, EcsServiceEvent, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService } from '../../core/services/monitoring.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'services' | 'activity';

const DEFAULT_SORT = 'name-asc';
const FACET_KEYS   = ['status', 'launchType', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'name-asc',        label: 'Name A → Z'      },
  { value: 'name-desc',       label: 'Name Z → A'      },
  { value: 'cluster-asc',     label: 'Cluster'         },
  { value: 'monitored-first', label: 'Monitored first' },
];

const FACETS: FacetDef[] = [
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'ACTIVE',   label: 'Active'   },
      { value: 'DRAINING', label: 'Draining' },
      { value: 'INACTIVE', label: 'Inactive' },
    ],
  },
  {
    key: 'launchType',
    label: 'Launch Type',
    options: [
      { value: 'FARGATE',  label: 'Fargate'  },
      { value: 'EC2',      label: 'EC2'      },
      { value: 'EXTERNAL', label: 'External' },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

@Component({
  selector: 'app-ecs-detail',
  templateUrl: './ecs-detail.component.html',
})
export class EcsDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  ecsServices: EcsService[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  selectedSvc: string | null = null;
  svcEvents: EcsServiceEvent[] = [];
  svcEventsLoading = false;
  svcEventsError: string | null = null;
  svcPage = 1;
  readonly svcPageSize = 10;

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview',  label: 'Overview' },
    { id: 'services',  label: 'Services' },
    { id: 'activity',  label: 'Activity' },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'ecs', name: 'ECS', icon: 'box', route: '/cloud/ecs', status: 'healthy', metrics: [] };

  drawerItemId: string | null = null;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;

  private sub?: Subscription;
  private routeSub?: Subscription;
  private svcSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('ecs').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      this.filters = parseFiltersFromParams(params as Record<string, string>, DEFAULT_SORT, FACET_KEYS);
      const svc = (params as Record<string, string>)['svc'] ?? null;
      if (svc !== this.selectedSvc) {
        this.selectedSvc = svc;
        if (svc) { this.svcPage = 1; this.loadSvcEvents(svc); }
        else { this.svcSub?.unsubscribe(); this.svcEvents = []; }
      }
    });
    this.loadData(false);
  }

  /** Loads the service list (cache-aware), monitoring state (monitored count), and activity findings. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getEcsServices(bypass).subscribe(svcs => {
      this.ecsServices = svcs;
      this.loading     = false;
      this.refreshing  = false;
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.activityLoading = false;
      this.activity        = sortActivity(findingsToActivity(findings, 'ECS_SERVICE'));
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.svcSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'services';
    this.selectSvc(item.resourceId);
  }

  // ── Summary counts (standardized: total + monitored) ────────────────────────

  get totalCount():     number { return this.ecsServices.length; }
  get monitoredCount(): number { return this.ecsServices.filter(s => this.cloud.isMonitored('ecs', s.arn)).length; }

  // ── Overview counts (unfiltered) ────────────────────────────────────────────

  get activeCount():   number { return this.ecsServices.filter(s => s.status === 'ACTIVE').length; }
  get warningCount():  number { return this.ecsServices.filter(s => s.healthStatus === 'warning').length; }
  get drainingCount(): number { return this.ecsServices.filter(s => s.status === 'DRAINING').length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredServices(): EcsService[] {
    let result = this.ecsServices;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(s =>
      s.name.toLowerCase().includes(q) ||
      s.arn.toLowerCase().includes(q) ||
      s.clusterName.toLowerCase().includes(q),
    );

    const statuses = this.filters.facets['status'] ?? [];
    if (statuses.length) result = result.filter(s => statuses.includes(s.status));

    const launchTypes = this.filters.facets['launchType'] ?? [];
    if (launchTypes.length) result = result.filter(s => launchTypes.includes(s.launchType));

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(s => {
        const mon = this.cloud.isMonitored('ecs', s.arn);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(svcs: EcsService[], sort: string): EcsService[] {
    return [...svcs].sort((a, b) => {
      switch (sort) {
        case 'name-desc':     return b.name.localeCompare(a.name);
        case 'cluster-asc':   return a.clusterName.localeCompare(b.clusterName) || a.name.localeCompare(b.name);
        case 'monitored-first': {
          const aM = this.cloud.isMonitored('ecs', a.arn) ? 0 : 1;
          const bM = this.cloud.isMonitored('ecs', b.arn) ? 0 : 1;
          return aM - bM;
        }
        default: return a.name.localeCompare(b.name);
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Drill-down ──────────────────────────────────────────────────────────────

  selectSvc(arn: string): void {
    this.svcPage = 1;
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { svc: arn },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  closeSvc(): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { svc: null },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  private loadSvcEvents(arn: string): void {
    this.svcEventsLoading = true;
    this.svcEventsError   = null;
    this.svcSub?.unsubscribe();
    this.svcSub = this.cloud.getEcsServiceEvents(arn).subscribe({
      next:  evs   => { this.svcEvents = evs; this.svcEventsLoading = false; },
      error: ()    => { this.svcEventsError = 'Failed to load service events.'; this.svcEventsLoading = false; },
    });
  }

  get pagedSvcEvents(): EcsServiceEvent[] {
    const start = (this.svcPage - 1) * this.svcPageSize;
    return this.svcEvents.slice(start, start + this.svcPageSize);
  }

  get svcPageTotal(): number { return Math.ceil(this.svcEvents.length / this.svcPageSize); }

  // ── Monitoring ──────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(arn: string): boolean { return this.cloud.isMonitored('ecs', arn); }

  toggleMonitoring(arn: string): void {
    const current = this.cloud.isMonitored('ecs', arn);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('ecs', arn, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(arn: string): void  { this.drawerItemId = arn; }
  closeItemSettings(): void            { this.drawerItemId = null; }

  itemOverrideSummary(arn: string): string {
    return this.cloud.getItemOverrideSummary('ecs', arn);
  }

  // ── Status badge ────────────────────────────────────────────────────────────

  statusClass(status: string): string {
    const map: Record<string, string> = {
      ACTIVE:   'bg-emerald-100 text-emerald-700',
      DRAINING: 'bg-amber-100 text-amber-700',
      INACTIVE: 'bg-slate-100 text-slate-400',
    };
    return map[status] ?? 'bg-slate-100 text-slate-400';
  }

  healthClass(healthStatus: EcsService['healthStatus']): string {
    const map: Record<string, string> = {
      healthy: 'bg-emerald-100 text-emerald-700',
      warning: 'bg-amber-100 text-amber-700',
      unknown: 'bg-slate-100 text-slate-400',
    };
    return map[healthStatus] ?? 'bg-slate-100 text-slate-400';
  }
}
